import { PrimaryButton } from '@acko-tech/building-blocks.ui.button';
import Image from 'next/image';

import { getImageBasePath } from 'utils/generics';

import * as Styles from './styles';
import { AssetDetails, CustomerDetails, ModelDetails } from '../api';
import { EditableFieldKey } from '../validators';
import EditableFieldRow from './EditableFieldRow';

const getDisplayValue = (value?: string) =>
    value && value.trim().length ? value : '-';

type DetailsCardProps = {
    isButtonDisabled: boolean;
    onSubmit: () => void;
    isLoading: boolean;
    vehicleDetails: AssetDetails;
    customerDetails: CustomerDetails;
    modelDetails: ModelDetails;
    editingFields: Set<EditableFieldKey>;
    fieldErrors: Record<EditableFieldKey, string>;
    onCustomerFieldChange: (
        field: keyof CustomerDetails,
        value: string
    ) => void;
    onVehicleFieldChange: (
        field: 'rto_code' | 'charger_number',
        value: string
    ) => void;
    onEditField: (field: EditableFieldKey) => void;
    onSaveField: (field: EditableFieldKey) => void;
    onFieldFocus?: (field: EditableFieldKey) => void;
};

function DetailsCard({
    isButtonDisabled,
    onSubmit,
    isLoading,
    vehicleDetails,
    customerDetails,
    modelDetails,
    editingFields,
    fieldErrors,
    onCustomerFieldChange,
    onVehicleFieldChange,
    onEditField,
    onSaveField,
    onFieldFocus,
}: DetailsCardProps) {
    const isEditing = (field: EditableFieldKey) => editingFields.has(field);

    return (
        <Styles.DetailsCard>
            <Styles.DetailsCardHeader>
                <Image
                    src={getImageBasePath('/icons/greenTick.svg')}
                    width={40}
                    height={40}
                />
                <Styles.DetailsCardTitle>
                    <Styles.DetailsCardTitleText>
                        Vehicle Validated Successfully
                    </Styles.DetailsCardTitleText>
                    <Styles.TitleSubText>
                        Eligible for Extended Warranty
                    </Styles.TitleSubText>
                </Styles.DetailsCardTitle>
            </Styles.DetailsCardHeader>

            <Styles.SectionHeading>Vehicle details</Styles.SectionHeading>

            <Styles.CardRow>
                <Styles.CardRowLabel>VIN Number:</Styles.CardRowLabel>
                <Styles.CardRowValueText>
                    {getDisplayValue(vehicleDetails?.asset_number)}
                </Styles.CardRowValueText>
            </Styles.CardRow>
            <Styles.CardRow>
                <Styles.CardRowLabel>Vehicle Model:</Styles.CardRowLabel>
                <Styles.CardRowValueText>
                    {getDisplayValue(modelDetails?.name)}
                </Styles.CardRowValueText>
            </Styles.CardRow>
            <Styles.CardRow>
                <Styles.CardRowLabel>Purchase/Sale Date:</Styles.CardRowLabel>
                <Styles.CardRowValueText>
                    {getDisplayValue(vehicleDetails?.purchase_date)}
                </Styles.CardRowValueText>
            </Styles.CardRow>
            <Styles.CardRow>
                <Styles.CardRowLabel>
                    Registration No. (Optional):
                </Styles.CardRowLabel>
                <Styles.CardRowValueText>
                    {getDisplayValue(vehicleDetails?.registration_number)}
                </Styles.CardRowValueText>
            </Styles.CardRow>
            <Styles.CardRow>
                <Styles.CardRowLabel>Motor Number:</Styles.CardRowLabel>
                <Styles.CardRowValueText>
                    {getDisplayValue(vehicleDetails?.motor_number)}
                </Styles.CardRowValueText>
            </Styles.CardRow>
            <Styles.CardRow>
                <Styles.CardRowLabel>Battery No. 1:</Styles.CardRowLabel>
                <Styles.CardRowValueText>
                    {getDisplayValue(vehicleDetails?.bin)}
                </Styles.CardRowValueText>
            </Styles.CardRow>
            <Styles.CardRow>
                <Styles.CardRowLabel>Battery No. 2:</Styles.CardRowLabel>
                <Styles.CardRowValueText>
                    {getDisplayValue(vehicleDetails?.bin2)}
                </Styles.CardRowValueText>
            </Styles.CardRow>

            <EditableFieldRow
                label="RTO Code"
                value={vehicleDetails.rto_code}
                isEditing={isEditing('rto_code')}
                placeholder="Enter RTO code"
                error={fieldErrors.rto_code}
                onChange={(value) => onVehicleFieldChange('rto_code', value)}
                onEdit={() => onEditField('rto_code')}
                onSave={() => onSaveField('rto_code')}
                onFocus={() => onFieldFocus?.('rto_code')}
            />
            <EditableFieldRow
                label="Charger Number"
                value={vehicleDetails.charger_number}
                isEditing={isEditing('charger_number')}
                placeholder="Enter charger number"
                error={fieldErrors.charger_number}
                onChange={(value) =>
                    onVehicleFieldChange('charger_number', value)
                }
                onEdit={() => onEditField('charger_number')}
                onSave={() => onSaveField('charger_number')}
                onFocus={() => onFieldFocus?.('charger_number')}
            />

            <Styles.SectionHeading>Customer details</Styles.SectionHeading>

            <Styles.CardRow>
                <Styles.CardRowLabel>Full Name:</Styles.CardRowLabel>
                <Styles.CardRowValueText>
                    {getDisplayValue(customerDetails?.name)}
                </Styles.CardRowValueText>
            </Styles.CardRow>
            <EditableFieldRow
                label="Mobile No."
                value={customerDetails.phone}
                isEditing={isEditing('phone')}
                inputType="number"
                placeholder="Enter mobile number"
                error={fieldErrors.phone}
                onChange={(value) => onCustomerFieldChange('phone', value)}
                onEdit={() => onEditField('phone')}
                onSave={() => onSaveField('phone')}
                onFocus={() => onFieldFocus?.('phone')}
            />
            <EditableFieldRow
                label="Email ID"
                value={customerDetails.email}
                isEditing={isEditing('email')}
                placeholder="Enter email ID"
                error={fieldErrors.email}
                onChange={(value) => onCustomerFieldChange('email', value)}
                onEdit={() => onEditField('email')}
                onSave={() => onSaveField('email')}
                onFocus={() => onFieldFocus?.('email')}
            />
            <EditableFieldRow
                label="Address"
                value={customerDetails.address}
                isEditing={isEditing('address')}
                placeholder="Enter address"
                error={fieldErrors.address}
                onChange={(value) => onCustomerFieldChange('address', value)}
                onEdit={() => onEditField('address')}
                onSave={() => onSaveField('address')}
                onFocus={() => onFieldFocus?.('address')}
            />
            <EditableFieldRow
                label="Pin Code"
                value={customerDetails.pincode}
                isEditing={isEditing('pincode')}
                inputType="number"
                placeholder="Enter pin code"
                error={fieldErrors.pincode}
                onChange={(value) => onCustomerFieldChange('pincode', value)}
                onEdit={() => onEditField('pincode')}
                onSave={() => onSaveField('pincode')}
                onFocus={() => onFieldFocus?.('pincode')}
            />

            <Styles.ButtonWrapper>
                <PrimaryButton
                    disabled={isButtonDisabled}
                    onClick={onSubmit}
                    loading={isLoading}
                >
                    Continue to plan selection
                </PrimaryButton>
            </Styles.ButtonWrapper>
        </Styles.DetailsCard>
    );
}

export default DetailsCard;
